import {
  signalStore,
  withState,
  withMethods,
  patchState
} from "@ngrx/signals";
import {
  FilterSet,
  FilterSetType,
} from "../../core/interfaces/filter-interface";
import { JobCardInterface } from "src/app/core/interfaces/job-card-interface";
import { JobService } from "../services/mobility/job-service";
import { inject } from "@angular/core";
import { FiltersService } from "src/app/core/services/filters/filters-service";
import { Pagination } from "src/app/core/interfaces/pagination";

interface JobSearchState {
  branchesFilters: FilterSet;
  branchesFiltersSelected: FilterSet;
  activitiesFilters: FilterSet;
  activitiesFiltersSelected: FilterSet;
  locationFilters: FilterSet;
  locationFiltersSelected: FilterSet;
  locationSearchValue: string | null;
  locationSearchValueSelected: string | null;
  results: JobCardInterface[];
  pagination: Pagination;
}

const initialState: JobSearchState = {
  branchesFilters: null,
  branchesFiltersSelected: null,
  activitiesFilters: null,
  activitiesFiltersSelected: null,
  locationFilters: null,
  locationFiltersSelected: null,
  locationSearchValue: null,
  locationSearchValueSelected: null,
  results: [],
  pagination: null,
};

export const JobSearchStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withMethods((store) => {
    const jobService = inject(JobService);
    const filtersService = inject(FiltersService);

    return {
      setFilters(
        locationFilters,
        branchesFilters,
        activitiesFilters,
        locationSearchValue
      ): void {
        patchState(store, {
          locationFilters,
          branchesFilters,
          activitiesFilters,
          locationSearchValue,
        });
      },
      setFiltersSelected(
        locationFiltersSelected: FilterSet,
        branchesFiltersSelected: FilterSet,
        activitiesFiltersSelected: FilterSet,
        locationSearchValueSelected: string | null
      ): void {
        patchState(store, {
          locationFiltersSelected,
          branchesFiltersSelected,
          activitiesFiltersSelected,
          locationSearchValueSelected,
        });
      },
      locationFilterExists(): boolean {
        return (
          store.locationFilters() !== null ||
          (store.locationSearchValueSelected() !== null &&
            store.locationSearchValueSelected().length > 0)
        );
      },
      branchesFilterExists(): boolean {
        return store.branchesFilters() !== null;
      },
      activitiesFilterExists(): boolean {
        return store.activitiesFilters() !== null;
      },
      anyResults(): boolean {
        return store.results().length > 0;
      },
      setResults(results: JobCardInterface[]) {
        patchState(store, { results });
      },
      setPagination(pagination: Pagination) {
        patchState(store, { pagination });
      },
      async loadJobFilters(): Promise<void> {
        const filterGroup = await filtersService.getFilterGroup();

        const filterSetGeographicalLocation =
          await filtersService.getGeographicalLocationFilterSet();

        const filterSetSector = filterGroup.filterSets.find(
          (fs) => fs.filterSetType == FilterSetType.Sector
        );

        const filterSetActivity = await filtersService.getActivitiesFilterSetForVinciJob();

        filterSetSector.filtersCategories =
          filtersService.getAllFilterCategories(filterSetSector.filters);
        filterSetActivity.filtersCategories =
          filtersService.getAllFilterCategories(filterSetActivity.filters);
        filterSetGeographicalLocation.filtersCategories =
          filtersService.getTopLevelsGeographicalLocationFilterCategories(
            filterSetGeographicalLocation.filters
          );

        filterSetActivity.filters = filterSetActivity.filters.filter(
          (x) => x.filters === null
        );
        filterSetSector.filters = filterSetSector.filters.filter(
          (x) => x.filters === null
        );
        filterSetGeographicalLocation.filters =
          filterSetGeographicalLocation.filters.filter(
            (x) => x.filters === null
          );

        this.setFilters(
          filterSetGeographicalLocation,
          filterSetSector,
          filterSetActivity,
          ""
        );

        this.updateSelectedFiltersTranslations();
      },
      async getJobResults(): Promise<JobCardInterface[]> {
        const selectedFilters = [
          store.locationFiltersSelected(),
          store.branchesFiltersSelected(),
          store.activitiesFiltersSelected(),
        ];

        const currentPagination = store.pagination();
        const jobResults = await jobService.getJobsList(
          selectedFilters,
          store.locationSearchValueSelected(),
          currentPagination
        );
        const jobsList = jobResults.jobCards;

        this.setPagination(jobResults.pagination);
        return jobsList;
      },
      async loadJobs(): Promise<void> {
        const jobsList = await this.getJobResults();
        this.setResults(jobsList);
      },
      async loadMoreJobs(): Promise<void> {
        const jobsList = await this.getJobResults();

        this.setResults([...store.results(), ...jobsList]);
      },
      updateSelectedFiltersTranslations(): void {
        const locationFilters = store.locationFilters();
        const selectedLocationFilters = store.locationFiltersSelected();
        const branchesFilters = store.branchesFilters();
        const selectedBranchesFilters = store.branchesFiltersSelected();
        const activitiesFilters = store.activitiesFilters();
        const selectedActivitiesFilters = store.activitiesFiltersSelected();

        if (store.locationFiltersSelected()) {
          patchState(store, {
            locationFiltersSelected: filtersService.updateFilterSetTranslations(
              selectedLocationFilters,
              locationFilters
            ),
          });
        }

        if (store.branchesFiltersSelected()) {
          patchState(store, {
            branchesFiltersSelected: filtersService.updateFilterSetTranslations(
              selectedBranchesFilters,
              branchesFilters
            ),
          });
        }

        if (store.activitiesFiltersSelected()) {
          patchState(store, {
            activitiesFiltersSelected:
              filtersService.updateFilterSetTranslations(
                selectedActivitiesFilters,
                activitiesFilters
              ),
          });
        }
      },
    };
  })
);
